import React from 'react';
import { Container, Grid, Card, CardContent, Typography, ButtonGroup, Button, Divider, Paper, List, ListItem, ListItemText, CssBaseline, Box, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemIcon, FormGroup, FormControlLabel, Switch, TextField, Tooltip, IconButton, Chip, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

const humanizeActivity = (key) => {
  if (!key) return '';
  return key.toString().split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const StatusChip = ({ status }) => {
  if (!status) return null;
  const color = status === 'on_track' ? '#2e7d32' : '#c62828';
  const label = status === 'on_track' ? 'On Track' : 'At Risk';
  return <Chip size="small" label={label} style={{ backgroundColor: color, color: '#fff', marginLeft: 8 }} />;
};

const KpiCard = ({ label, value, suffix, tooltip, status }) => (
  <Card elevation={2}>
    <CardContent>
      <Box display="flex" alignItems="center" mb={0.5}>
        <Typography variant="overline" color="textSecondary">
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} placement="top" arrow>
            <IconButton size="small" aria-label={`${label} info`} style={{ marginLeft: 4 }}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <StatusChip status={status} />
      </Box>
      <Typography variant="h4" component="div">
        {typeof value === 'number' ? value : (value || 0)}{suffix || ''}
      </Typography>
    </CardContent>
  </Card>
);

const Section = ({ title, children, tooltip }) => (
  <Paper elevation={1} style={{ padding: 16 }}>
    <Box display="flex" alignItems="center" mb={1}>
      <Typography variant="subtitle1" gutterBottom style={{ marginBottom: 0 }}>{title}</Typography>
      {tooltip && (
        <Tooltip title={tooltip} placement="top" arrow>
          <IconButton size="small" aria-label={`${title} info`} style={{ marginLeft: 6 }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
    <Divider />
    <Box mt={2}>{children}</Box>
  </Paper>
);

const ActivityStatusChip = ({ status }) => {
  let color = '#9e9e9e';
  let label = 'No Goal';
  if (status === 'on_track') {
    color = '#2e7d32';
    label = 'On Track';
  } else if (status === 'at_risk') {
    color = '#c62828';
    label = 'At Risk';
  }
  return <Chip size="small" label={label} style={{ backgroundColor: color, color: '#fff' }} />;
};

const ActivityCard = ({ activityKey, data, color }) => {
  const pct = ((data?.rate || 0) * 100).toFixed(1);
  const adopted = data?.adopted || 0;
  const status = data?.goal?.status || null;
  return (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={0.5}>
          <Typography variant="overline" style={{ color }}>{humanizeActivity(activityKey)}</Typography>
        </Box>
        <Typography variant="h5" component="div">{pct}%</Typography>
        <Typography variant="body2" color="textSecondary">{adopted} adopted</Typography>
        <Box mt={1}>
          <ActivityStatusChip status={status} />
        </Box>
      </CardContent>
    </Card>
  );
};

const motivationOptions = [
  { label: 'Growth', value: 'growth' },
  { label: 'Succession', value: 'succession' },
  { label: 'Evaluating', value: 'no_motivation' },
];

const activityColors = {
  valuation: '#3f51b5',
  profile: '#9c27b0',
  discovery: '#009688',
  education: '#ff9800',
  consultation: '#f44336',
  deal_room: '#607d8b',
};

const activitiesOrder = ['valuation', 'profile', 'discovery', 'education', 'consultation', 'deal_room'];
const activityOptions = [
  { label: 'All Activities', value: '' },
  { label: 'Valuation', value: 'valuation' },
  { label: 'Profile', value: 'profile' },
  { label: 'Discovery', value: 'discovery' },
  { label: 'Education', value: 'education' },
  { label: 'Consultation', value: 'consultation' },
  { label: 'Deal Room', value: 'deal_room' },
];

const Dashboard = ({ kpis, breakdown, filters, onFiltersChange, table, onPageChange, goalStatus }) => {
  const setWindow = (days) => onFiltersChange({ ...filters, windowDays: days });

  const toggle = (key) => (e) => onFiltersChange({ [key]: e.target.checked });
  const update = (key) => (e) => onFiltersChange({ [key]: e.target.value });

  const onMotivationsChange = (e) => onFiltersChange({ motivations: e.target.value });

  const exportCsv = () => {
    const params = new URLSearchParams();
    params.append('start_date', filters.startDate);
    params.append('end_date', filters.endDate);
    (filters.motivations || []).forEach((m) => params.append('motivations[]', m));
    window.location.href = `/admin/motivation_adoption/export?${params.toString()}`;
  };

  const kpiHelp = {
    eligible: 'Number of users that match the selected cohort filters (motivation, hidden, profile/onboarding requirements, and onboarding date range).',
    adopted: 'Of the eligible users, how many completed any Getting Started activity within the selected window (7/14/30/60/90 days) from their onboarding completion date.',
    adoptionRate: 'Adoption percentage = Adopted / Eligible, for the selected window.',
    ttfuP50: 'Median time-to-first-use (days) from onboarding completion to the first Getting Started activity.',
    ttfuP75: '75th percentile time-to-first-use (days) from onboarding completion.',
    recency30d: 'Number of users with any Getting Started activity in the last 30 days relative to the selected end date.',
  };

  const filterHelp = {
    reportingRange: 'Start/End Date defines the reporting horizon. Metrics (like Recency 30d) are computed as of the End Date. Adoption windows (7/14/30/60/90) measure from each user\'s onboarding completion to first activity and are not clipped by the Start Date.',
    onboardingRange: 'Onboarding Start/End filters which users are included based on their onboarding_completed_at timestamp. This shapes the cohort, not the timing of their activities.',
    activityBreakdown: 'For each activity: adoption within the selected window (rate and count). Colors reflect goal status when a goal exists.',
    depthBuckets: 'Distribution of users by number of distinct Getting Started activities completed: 1, 2, or 3+ over their lifetime.',
    activityTable: 'Eligible users in the selected cohort. Adopted indicates first activity occurred within the selected window from onboarding. Check marks indicate whether each activity was ever completed.',
  };

  const renderMotivationValue = (selectedValues) => {
    const labels = (selectedValues || []).map((v) => motivationOptions.find((o) => o.value === v)?.label || v);
    return labels.join(', ');
  };

  const motivationLabel = (val) => motivationOptions.find(o => o.value === val)?.label || val;

  const page = (table?.page || 1) - 1;
  const perPage = table?.perPage || 25;

  const perActivity = breakdown?.per_activity || {};

  const [helpOpen, setHelpOpen] = React.useState(false);

  return (
    <React.Fragment>
      <CssBaseline />
      <Container maxWidth="lg" style={{ paddingTop: 24, paddingBottom: 48 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h5" component="h2">Getting Started Adoption</Typography>
          <Box>
            <Tooltip title="How to use this dashboard" placement="bottom" arrow>
              <IconButton aria-label="Help" onClick={() => setHelpOpen(true)} style={{ marginRight: 8 }}>
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
            <Button href="/admin/motivation_adoption_goals" variant="contained" color="primary" style={{ marginRight: 8 }}>Manage Goals</Button>
            <Button variant="contained" color="default" style={{ marginRight: 8 }} onClick={() => {
              const d = new Date(); d.setDate(d.getDate() - 1);
              const date = d.toISOString().slice(0,10);
              fetch('/admin/motivation_adoption/trigger_slack_digest', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content') },
                credentials: 'same-origin',
                body: new URLSearchParams({ date, window_days: String(filters.windowDays) }).toString(),
              });
            }}>Test Slack</Button>
            <Button variant="outlined" color="primary" onClick={exportCsv}>Export CSV</Button>
          </Box>
        </Box>

        <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Customer Success How-To</DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle1" gutterBottom>Find Succession users who created a Deal Room within 60 days</Typography>
            <ol>
              <li>Motivations: select Succession.</li>
              <li>Window: select 60d.</li>
              <li>Onboarding Start/End: set your date range.</li>
              <li>Activity: select Deal Room.</li>
              <li>Toggle Within Window Only on.</li>
              <li>Use the Eligible Users table (or Export CSV) as your working set.</li>
            </ol>
            <Typography variant="subtitle1" gutterBottom style={{ marginTop: 16 }}>Find users who edited Profile within 7 days (any motivation)</Typography>
            <ol>
              <li>Motivations: leave empty to include all (or pick specific ones).</li>
              <li>Window: select 7d.</li>
              <li>Onboarding Start/End: set your date range.</li>
              <li>Activity: select Profile.</li>
              <li>Toggle Within Window Only on.</li>
            </ol>
            <Typography variant="body2" color="textSecondary">Tip: Goals affect the on-track/at-risk status chips but not filtering. Set goals via Manage Goals for status overlays and Slack digests.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHelpOpen(false)} color="primary" autoFocus>Close</Button>
          </DialogActions>
        </Dialog>

        <Section title="Filters">
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Box display="flex" alignItems="center">
                <TextField
                  label="Start Date"
                  type="date"
                  value={filters.startDate}
                  onChange={update('startDate')}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <Tooltip title={filterHelp.reportingRange} placement="top" arrow>
                  <IconButton size="small" aria-label="Start/End Date help" style={{ marginLeft: 6 }}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="End Date"
                type="date"
                value={filters.endDate}
                onChange={update('endDate')}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="motivations-label">Motivations</InputLabel>
                <Select
                  labelId="motivations-label"
                  multiple
                  value={filters.motivations}
                  onChange={onMotivationsChange}
                  renderValue={renderMotivationValue}
                >
                  {motivationOptions.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>
                      <ListItemIcon>
                        <Checkbox checked={filters.motivations.indexOf(value) > -1} />
                      </ListItemIcon>
                      <ListItemText primary={label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" justifyContent="flex-end">
                <ButtonGroup variant="contained" color="primary" size="small">
                  {[7, 14, 30, 60, 90, 120].map((d) => (
                    <Button
                      key={d}
                      onClick={() => setWindow(d)}
                      variant={filters.windowDays === d ? 'contained' : 'outlined'}
                      color={filters.windowDays === d ? 'primary' : 'default'}
                    >
                      {d}d
                    </Button>
                  ))}
                </ButtonGroup>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="activity-label">Activity</InputLabel>
                <Select
                  labelId="activity-label"
                  value={filters.activity}
                  onChange={update('activity')}
                >
                  {activityOptions.map(({ label, value }) => (
                    <MenuItem key={value || 'all'} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormGroup row>
                <FormControlLabel
                  control={<Switch checked={!!filters.withinWindowOnly} onChange={toggle('withinWindowOnly')} color="primary" />}
                  label="Within Window Only"
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormGroup row>
                <FormControlLabel
                  control={<Switch checked={!!filters.includeHidden} onChange={toggle('includeHidden')} color="primary" />}
                  label="Include Hidden"
                />
                <FormControlLabel
                  control={<Switch checked={!!filters.requireMinimumProfile} onChange={toggle('requireMinimumProfile')} color="primary" />}
                  label="Require Minimum Profile"
                />
                <FormControlLabel
                  control={<Switch checked={!!filters.requireOnboardingCompleted} onChange={toggle('requireOnboardingCompleted')} color="primary" />}
                  label="Require Onboarding Completed"
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" alignItems="center">
                <TextField
                  label="Onboarding Start"
                  type="date"
                  value={filters.onboardingStartDate}
                  onChange={update('onboardingStartDate')}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <Tooltip title={filterHelp.onboardingRange} placement="top" arrow>
                  <IconButton size="small" aria-label="Onboarding range help" style={{ marginLeft: 6 }}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Onboarding End"
                type="date"
                value={filters.onboardingEndDate}
                onChange={update('onboardingEndDate')}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
          </Grid>
        </Section>

        <Box mt={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard label="Eligible" value={kpis?.eligible || 0} tooltip={kpiHelp.eligible} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard label="Adopted" value={kpis?.adopted || 0} tooltip={kpiHelp.adopted} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard label="Adoption %" value={((kpis?.adoption_rate || 0) * 100).toFixed(1)} suffix="%" tooltip={kpiHelp.adoptionRate} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard label="Median TTFU" value={kpis?.ttfu_p50 || 0} suffix="d" tooltip={kpiHelp.ttfuP50} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard label="TTFU p75" value={kpis?.ttfu_p75 || 0} suffix="d" tooltip={kpiHelp.ttfuP75} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard label="Recency 30d" value={kpis?.recency_30d || 0} tooltip={kpiHelp.recency30d} />
            </Grid>
          </Grid>
        </Box>

        <Box mt={3}>
          <Grid container spacing={3}>
            {activitiesOrder.map((k) => {
              const v = perActivity[k] || { adopted: 0, eligible: 0, rate: 0, goal: null };
              return (
                <Grid item xs={12} sm={6} md={4} lg={2} key={k}>
                  <ActivityCard activityKey={k} data={v} color={activityColors[k] || '#607d8b'} />
                </Grid>
              );
            })}
            {Object.keys(perActivity || {}).length === 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">No data</Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        <Box mt={4}>
          <Section title="Depth Buckets" tooltip={filterHelp.depthBuckets}>
            <List dense>
              {Object.entries(breakdown?.depth_buckets || {}).map(([k, v]) => (
                <ListItem key={k} divider>
                  <ListItemText primary={k} />
                  <Typography variant="body1">{v}</Typography>
                </ListItem>
              ))}
              {Object.keys(breakdown?.depth_buckets || {}).length === 0 && (
                <Typography variant="body2" color="textSecondary">No data</Typography>
              )}
            </List>
          </Section>
        </Box>

        <Box mt={4}>
          <Section title="Eligible Users" tooltip={filterHelp.activityTable}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Motivation</TableCell>
                  <TableCell>Onboarded</TableCell>
                  <TableCell>Adopted</TableCell>
                  {activitiesOrder.map((a) => (
                    <TableCell key={a}>{humanizeActivity(a)}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(table?.rows || []).map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{motivationLabel(r.motivation)}</TableCell>
                    <TableCell>{r.onboarding_completed_at || ''}</TableCell>
                    <TableCell>{r.adopted ? <CheckCircleOutlineIcon style={{ color: '#2e7d32' }} /> : <RadioButtonUncheckedIcon color="disabled" />}</TableCell>
                    {activitiesOrder.map((a) => {
                      const within = r.activities_within_window?.[a];
                      const ever = r.activities?.[a];
                      return (
                        <TableCell key={`${r.id}-${a}`}>
                          {within ? (
                            <CheckCircleOutlineIcon style={{ color: '#2e7d32' }} />
                          ) : ever ? (
                            <CheckCircleOutlineIcon style={{ color: activityColors[a] || '#2e7d2' }} />
                          ) : (
                            <RadioButtonUncheckedIcon color="disabled" />
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={table?.total || 0}
              page={page}
              onPageChange={(_, p) => onPageChange(p + 1, perPage)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(e) => onPageChange(1, parseInt(e.target.value, 10))}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Section>
        </Box>
      </Container>
    </React.Fragment>
  );
};

export default Dashboard; 